import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Compass, MessageCircle, Upload, ArrowRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { FIND_SUPPORT, SUPPORT_TYPES, FALLBACK_DEADLINES, WHATSAPP_URL } from "./data";
import { SectionHeading } from "./Services";
import { scrollToSection } from "@/lib/scrollTo";

export const FindSupport = () => {
  const [country, setCountry] = useState("");
  const [university, setUniversity] = useState("");
  const [course, setCourse] = useState("");
  const [support, setSupport] = useState("");
  const [deadline, setDeadline] = useState("");

  const universities = useMemo(
    () => FIND_SUPPORT.universities[country] || ["My university isn't listed"],
    [country]
  );
  const complete = country && university && course && support && deadline;

  const waText = encodeURIComponent(
    `Hi MyHomeworkAssist! I'm at ${university || "my university"} (${country || "my country"}), studying ${course || "my course"}. I need ${support || "academic support"} — deadline: ${deadline || "flexible"}. Could I get a quote?`
  );

  const selectCls = "border-[#D4AF37]/25 bg-white text-[#12294D]";
  const contentCls = "border-[#D4AF37]/25 bg-[#F3EEE2] text-[#12294D]";

  const fields = [
    { label: "Country", value: country, set: (v) => { setCountry(v); setUniversity(""); }, options: FIND_SUPPORT.countries, testid: "country" },
    { label: "University", value: university, set: setUniversity, options: universities, testid: "university", disabled: !country },
    { label: "Course", value: course, set: setCourse, options: FIND_SUPPORT.courses, testid: "course" },
    { label: "Type of Support", value: support, set: setSupport, options: SUPPORT_TYPES, testid: "support" },
    { label: "Deadline", value: deadline, set: setDeadline, options: FALLBACK_DEADLINES.map((d) => d.label), testid: "deadline" },
  ];

  return (
    <section id="find-support" className="border-y border-[#D4AF37]/10 bg-[#F3EEE2]" data-testid="find-support-section">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
        <SectionHeading id="find-support" overline="Find Your Support" title="Answer five questions." accent="We do the rest." />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
            className="luxury-card rounded-xl p-6 sm:p-10 lg:col-span-3"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.label} className="space-y-2">
                  <Label className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">{f.label}</Label>
                  <Select value={f.value} onValueChange={f.set} disabled={f.disabled}>
                    <SelectTrigger data-testid={`find-support-${f.testid}-select`} className={selectCls}>
                      <SelectValue placeholder={`Select ${f.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent className={contentCls}>
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o} data-testid={`find-support-${f.testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.33, 1, 0.68, 1] }}
            className="flex flex-col justify-between rounded-xl border border-[#D4AF37]/40 bg-white p-6 sm:p-10 lg:col-span-2"
            data-testid="find-support-result"
          >
            <div>
              <p className="flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#A07E1F]">
                <Compass size={14} /> Your matched support
              </p>
              <p className="mt-5 font-serif-display text-2xl font-medium leading-snug text-[#12294D]" data-testid="find-support-summary">
                {complete
                  ? `${support} for ${course} at ${university} — due ${deadline}.`
                  : "Select your country, university, course, support type and deadline to see your match."}
              </p>
            </div>
            <div className="mt-8 flex flex-col gap-3">
              <button
                onClick={() => scrollToSection("#quote")}
                disabled={!complete}
                data-testid="find-support-quote-button"
                className="group flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-6 py-4 font-mono-accent text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A1E3C] transition-colors duration-300 enabled:hover:bg-[#C9A227] disabled:opacity-40"
              >
                Get a Free Quote <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => scrollToSection("#upload-brief")}
                disabled={!complete}
                data-testid="find-support-upload-button"
                className="flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/50 px-6 py-4 font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#A07E1F] transition-colors duration-300 enabled:hover:bg-[#D4AF37]/10 disabled:opacity-40"
              >
                <Upload size={15} /> Upload Your Brief
              </button>
              <a
                href={`${WHATSAPP_URL}?text=${waText}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="find-support-whatsapp-button"
                className="flex items-center justify-center gap-2 px-6 py-3 font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#46586F] transition-colors duration-300 hover:text-[#A07E1F]"
              >
                <MessageCircle size={15} /> Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
